import React, { useEffect, useState } from 'react';
import {
  Box,
  IconButton,
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
  Skeleton,
  Card,
  CardMedia,
  CardContent,
  Grid,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  ReceiptLong as ReceiptIcon,
  Delete as DeleteIcon,
  Fullscreen as FullscreenIcon
} from '@mui/icons-material';
import { getReceiptSignedUrl } from '../../services/expenseService';
// Helper function to format file size
const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const SecureReceiptViewer = ({ receipts, onDelete, showDelete = false }) => {
  const [signedUrls, setSignedUrls] = useState({});
  const [previewUrls, setPreviewUrls] = useState({});
  const [loading, setLoading] = useState({});
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Fetch signed URLs for all receipts
  useEffect(() => {
    const fetchSignedUrls = async () => {
      const urlsToFetch = receipts.filter(receipt => receipt.key && !signedUrls[receipt.key]);
      
      for (const receipt of urlsToFetch) {
        setLoading(prev => ({ ...prev, [receipt.key]: true }));
        try {
          // Get original URL for download
          const signedUrl = await getReceiptSignedUrl(receipt.key);
          console.log('[SecureReceiptViewer] Fetched signed URL for:', receipt.fileName, signedUrl);
          setSignedUrls(prev => ({ ...prev, [receipt.key]: signedUrl }));
          
          // Get preview URL if available
          if (receipt.versions && receipt.versions.preview && receipt.versions.preview.key) {
            const previewUrl = await getReceiptSignedUrl(receipt.versions.preview.key);
            console.log('[SecureReceiptViewer] Fetched preview URL for:', receipt.fileName, previewUrl);
            setPreviewUrls(prev => ({ ...prev, [receipt.key]: previewUrl }));
          } else {
            // Use original as preview if no preview version
            setPreviewUrls(prev => ({ ...prev, [receipt.key]: signedUrl }));
          }
        } catch (error) {
          console.error('[SecureReceiptViewer] Error fetching signed URL:', error);
          // Set empty string to avoid retry on error
          setSignedUrls(prev => ({ ...prev, [receipt.key]: '' }));
          setPreviewUrls(prev => ({ ...prev, [receipt.key]: '' }));
        } finally {
          setLoading(prev => ({ ...prev, [receipt.key]: false }));
        }
      }
    };

    if (receipts && receipts.length > 0) {
      fetchSignedUrls();
    }
  }, [receipts]); // Only depend on receipts array

  const handleReceiptClick = (receipt) => {
    setSelectedReceipt(receipt);
    setDialogOpen(true);
  };

  const handleDownload = async (receipt) => {
    const url = signedUrls[receipt.key];
    console.log('[SecureReceiptViewer] Downloading:', receipt.fileName, 'from:', url);
    
    if (url) {
      try {
        // Try direct download first
        const link = document.createElement('a');
        link.href = url;
        link.download = receipt.fileName;
        link.target = '_blank'; // Open in new tab if download fails
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error('[SecureReceiptViewer] Download error:', error);
        // If direct download fails, open in new tab
        window.open(url, '_blank');
      }
    } else {
      console.error('[SecureReceiptViewer] No URL available for download');
    }
  };

  const handleDelete = (e, receipt) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(receipt);
    }
  };

  const isImage = (mimeType) => {
    return mimeType && mimeType.startsWith('image/');
  };

  if (!receipts || receipts.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 3 }}>
        <ReceiptIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
        <Typography color="textSecondary">No receipts uploaded</Typography>
      </Box>
    );
  }

  return (
    <>
      <Grid container spacing={2}>
        {receipts.map((receipt) => (
          <Grid item xs={12} sm={6} md={4} key={receipt._id || receipt.key}>
            <Card
              sx={{
                cursor: 'pointer',
                transition: 'transform 0.2s',
                '&:hover': { transform: 'translateY(-4px)' }
              }}
              onClick={() => handleReceiptClick(receipt)}
            >
              {loading[receipt.key] ? (
                <Skeleton variant="rectangular" height={140} />
              ) : isImage(receipt.mimeType) && previewUrls[receipt.key] ? (
                <CardMedia
                  component="img"
                  height="140"
                  image={previewUrls[receipt.key]}
                  alt={receipt.fileName}
                  sx={{ objectFit: 'cover' }}
                />
              ) : (
                <Box
                  sx={{
                    height: 140,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'background.default'
                  }}
                >
                  <ReceiptIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                </Box>
              )}
              <CardContent sx={{ pb: 1 }}>
                <Typography variant="body2" noWrap>
                  {receipt.fileName}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {formatFileSize(receipt.fileSize)}
                </Typography>
                <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownload(receipt);
                    }}
                  >
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                  {showDelete && (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => handleDelete(e, receipt)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Full-screen dialog for viewing receipt */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {selectedReceipt?.fileName}
          </Typography>
          <IconButton onClick={() => setDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedReceipt && (
            <Box sx={{ textAlign: 'center' }}>
              {loading[selectedReceipt.key] ? (
                <CircularProgress />
              ) : isImage(selectedReceipt.mimeType) && previewUrls[selectedReceipt.key] ? (
                <img
                  src={previewUrls[selectedReceipt.key]}
                  alt={selectedReceipt.fileName}
                  style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                />
              ) : (
                <Box sx={{ py: 8 }}>
                  <ReceiptIcon sx={{ fontSize: 120, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="h6" color="textSecondary" gutterBottom>
                    Receipt Preview Not Available
                  </Typography>
                  <Typography color="textSecondary">
                    This file type cannot be previewed
                  </Typography>
                  <Chip
                    label={selectedReceipt.mimeType}
                    size="small"
                    sx={{ mt: 2 }}
                  />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SecureReceiptViewer;